// @ts-check

import paypalEngine from '../../../engines/paypal';

/**
 * @typedef {Object} PaypalInfo
 * @property {string} payment_type - Payment type ('paypal')
 * @property {string} payment_engine - Payment engine ('paypal')
 * @property {string} payment_provider - Payment provider ('paypal')
 * @property {string} tracker_id - Tracking identifier
 * @property {string} device_session_id - Device session identifier
 * @property {number} monthly_selected_plan - Selected monthly plan
 */

/**
 * Interface for a PayPal strategy.
 * @typedef {Object} PaypalStrategy
 * @property {function(PaypalInfo): Promise<Object>} createPayload - Creates the payload for the PayPal payment
 * @property {function(Object): boolean} validateConfig - Validates PayPal configuration
 * @property {function(Object, string|null=): Promise<{methods: Object}>} checkEligibility - Checks PayPal eligibility
 * @property {function(Object, Object, Object, string|null=): Promise<Object>} processPayment - Processes PayPal payment
 */

/**
 * Base PayPal strategy implementation.
 * @function PaypalStrategy
 * @returns {PaypalStrategy}
 */
function PaypalStrategy() {
  /**
   * Creates the payload for PayPal payment to be sent to Purchase API.
   * @param {PaypalInfo} paypalInfo - PayPal payment information
   * @returns {Promise<Object>} The created payload for Purchase API
   * @throws {Error} If required fields are missing
   */
  const createPayload = async (paypalInfo) => {
    // Validate required fields
    const requiredFields = ['payment_type', 'payment_engine', 'payment_provider'];
    const missingFields = requiredFields.filter((field) => !paypalInfo[field]);

    if (missingFields.length > 0) {
      throw new Error(`Missing required PayPal fields: ${missingFields.join(', ')}`);
    }

    // Validate payment method configuration
    if (paypalInfo.payment_type !== 'paypal') {
      throw new Error(`Invalid payment_type. Expected 'paypal', got '${paypalInfo.payment_type}'`);
    }

    if (paypalInfo.payment_engine !== 'paypal') {
      throw new Error(
        `Invalid payment_engine. Expected 'paypal', got '${paypalInfo.payment_engine}'`,
      );
    }

    if (paypalInfo.payment_provider !== 'paypal') {
      throw new Error(
        `Invalid payment_provider. Expected 'paypal', got '${paypalInfo.payment_provider}'`,
      );
    }

    // Build and return payload for Purchase API
    return {
      payment_type: paypalInfo.payment_type,
      payment_engine: paypalInfo.payment_engine,
      payment_provider: paypalInfo.payment_provider,
      tracker_id: paypalInfo.tracker_id,
      device_session_id: paypalInfo.device_session_id,
      monthly_selected_plan: paypalInfo.monthly_selected_plan || 0,
    };
  };

  /**
   * Validates the PayPal configuration
   * @param {Object} config - Configuration object to validate
   * @returns {boolean} True if configuration is valid
   * @throws {Error} If configuration is invalid
   */
  const validateConfig = (config) => {
    if (!config) {
      throw new Error('PayPal configuration is required');
    }

    if (config.sandbox !== undefined && typeof config.sandbox !== 'boolean') {
      throw new Error('PayPal sandbox configuration must be a boolean');
    }

    if (
      config.presentationMode &&
      !['auto', 'modal', 'popup', 'redirect'].includes(config.presentationMode)
    ) {
      throw new Error(
        'Invalid PayPal presentationMode. Must be one of: auto, modal, popup, redirect',
      );
    }

    if (config.currency !== undefined && typeof config.currency !== 'string') {
      throw new Error('PayPal currency, when provided, must be a string');
    }

    return true;
  };

  /**
   * Checks if PayPal is eligible for the current user/session.
   * @param {Object} config - PayPal configuration
   * @param {string|null} clientToken - PayPal client token (required only if SDK is not initialized)
   * @returns {Promise<{methods: Object}>} Eligibility result with details for all payment methods
   */
  const checkEligibility = async (config, clientToken = null) => {
    if (!config) {
      throw new Error('PayPal configuration not set');
    }

    try {
      // Initialize SDK if not already initialized (idempotent, handles race conditions)
      if (!paypalEngine.isInitialized()) {
        // Validate clientToken only when SDK initialization is needed
        if (!clientToken) {
          throw new Error('PayPal clientToken is required to initialize SDK');
        }

        // initialize() is idempotent and handles race conditions internally
        await paypalEngine.initialize({
          clientToken,
          sandbox: config.sandbox,
          sdkUrl: config.sdkUrl,
          components: ['paypal-payments'],
          locale: config.locale || 'es-MX',
          pageType: 'checkout',
        });
      }

      // Check eligibility using SDK with currency code
      const currency = config.currency || 'MXN';
      const eligibilityMethods = await paypalEngine.findEligibleMethods(currency);

      // Check each payment method and get details
      const methods = {
        paypal: {
          isEligible: eligibilityMethods.isEligible('paypal'),
        },
        paylater: {
          isEligible: eligibilityMethods.isEligible('paylater'),
          ...(eligibilityMethods.isEligible('paylater')
            ? eligibilityMethods.getDetails('paylater')
            : {}),
        },
        credit: {
          isEligible: eligibilityMethods.isEligible('credit'),
          ...(eligibilityMethods.isEligible('credit')
            ? eligibilityMethods.getDetails('credit')
            : {}),
        },
      };

      return { methods };
    } catch (error) {
      console.error('PayPal eligibility check failed:', error);
      throw new Error(`Eligibility check failed: ${error.message}`);
    }
  };

  /**
   * Processes a PayPal payment by initializing the SDK and starting the payment flow.
   * @param {Object} config - PayPal configuration
   * @param {Object} paymentData - Payment data
   * @param {Object} callbacks - PayPal session callbacks
   * @param {string|null} clientToken - PayPal client token (required only if SDK is not initialized)
   * @returns {Promise<Object>} Payment session object
   * @throws {Error} If payment processing fails
   */
  const processPayment = async (config, paymentData, callbacks, clientToken = null) => {
    // Validate dependencies
    if (!config) {
      throw new Error('PayPal configuration not set');
    }

    if (!callbacks || !callbacks.onApprove) {
      throw new Error('PayPal onApprove callback is required');
    }

    try {
      // Initialize SDK if not already initialized (idempotent, handles race conditions)
      if (!paypalEngine.isInitialized()) {
        // Validate clientToken only when SDK initialization is needed
        if (!clientToken) {
          throw new Error('PayPal clientToken is required to initialize SDK');
        }

        // initialize() is idempotent and handles race conditions internally
        await paypalEngine.initialize({
          clientToken,
          sandbox: config.sandbox,
          sdkUrl: config.sdkUrl,
          components: ['paypal-payments'],
          locale: config.locale || 'es-MX',
          pageType: 'checkout',
        });
      }

      // Check eligibility before processing (pass clientToken for consistency)
      const eligibility = await checkEligibility(config, clientToken);

      // Create payment session based on type
      let paymentSession;
      const { paymentType = 'standard', productCode } = paymentData;

      switch (paymentType) {
        case 'paylater':
          if (!eligibility.methods.paylater?.isEligible) {
            throw new Error('Pay Later is not eligible');
          }
          paymentSession = paypalEngine.createPayLaterOneTimePaymentSession(callbacks, productCode);
          break;

        case 'credit':
          if (!eligibility.methods.credit?.isEligible) {
            throw new Error('PayPal Credit is not eligible');
          }
          paymentSession = paypalEngine.createPayPalCreditOneTimePaymentSession(callbacks);
          break;

        case 'standard':
        default:
          if (!eligibility.methods.paypal?.isEligible) {
            throw new Error('PayPal is not eligible');
          }
          paymentSession = paypalEngine.createPayPalOneTimePaymentSession(callbacks);
          break;
      }

      // Start payment flow
      const options = {
        presentationMode: config.presentationMode || 'auto',
      };

      await paypalEngine.startPayment(paymentSession, options, paymentData.orderIdPromise);

      return paymentSession;
    } catch (error) {
      throw new Error(`PayPal payment processing failed: ${error.message}`);
    }
  };

  return {
    createPayload,
    validateConfig,
    checkEligibility,
    processPayment,
  };
}

const paypalStrategy = PaypalStrategy();

export default paypalStrategy;
